import { motion } from "framer-motion";
import { ReactNode } from "react";

import { splitStega } from "@/lib/sanity/stega";

type AnimationType = "blurInUp" | "slideInUp" | "fadeIn" | "slideInLeft";

interface TextAnimateProps {
  children: ReactNode;
  animation?: AnimationType;
  by?: "word" | "character" | "line";
  once?: boolean;
  className?: string;
  as?: "h1" | "h2" | "h3" | "p" | "span";
  delay?: number;
}

const animationVariants = {
  blurInUp: {
    hidden: { opacity: 0, filter: "blur(4px)", y: 10 },
    visible: { opacity: 1, filter: "blur(0px)", y: 0 },
  },
  slideInUp: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  },
  slideInLeft: {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
};

export function TextAnimate({
  children,
  animation = "blurInUp",
  by = "character",
  once = true,
  className = "",
  as = "span",
  delay = 0,
}: TextAnimateProps) {
  const text = typeof children === "string" ? children : "";
  const { cleaned, encoded } = splitStega(text);
  const splitSource = cleaned || text;
  const baseSegments =
    by === "line"
      ? splitSource.split("\n")
      : by === "character"
        ? splitSource.split("")
        : splitSource.split(" ");
  const segments = baseSegments.length > 0 ? [...baseSegments] : [splitSource];
  if (encoded && segments.length > 0) {
    segments[0] = `${segments[0]}${encoded}`;
  }
  const selectedAnimation = animationVariants[animation];
  const Component: TextAnimateProps["as"] = as;

  if (typeof children !== "string") {
    return (
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once }}
        transition={{ duration: 0.5 }}
        variants={selectedAnimation}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <Component className={className}>
      {segments.map((segment, i) => (
        <motion.span
          key={i}
          initial="hidden"
          animate="visible"
          variants={selectedAnimation}
          transition={{
            duration: 0.3,
            delay: delay + i * 0.04,
            ease: [0.25, 0.4, 0.25, 1],
          }}
          style={{
            display: by === "line" ? "block" : "inline-block",
            marginRight: by === "word" ? "0.25em" : "0",
          }}
        >
          {segment === " " ? "\u00A0" : segment}
        </motion.span>
      ))}
    </Component>
  );
}
